import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface CustomField {
  id: string;
  account_id: string;
  name: string;
  field_type: 'text' | 'number' | 'date' | 'select' | 'multi_select';
  options?: string[];
  is_required: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useCustomFields() {
  const { user } = useAuth();
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFields = useCallback(async () => {
    if (!user?.account?.id || !supabase) return;

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('account_fields')
        .select('*')
        .eq('account_id', user.account.id)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setFields(data || []);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.account?.id]);

  const createField = async (field: Omit<CustomField, 'id' | 'account_id' | 'created_at' | 'updated_at'>) => {
    if (!user?.account?.id || !supabase) return null;

    try {
      const { data, error: createError } = await supabase
        .from('account_fields')
        .insert({
          account_id: user.account.id,
          ...field
        })
        .select()
        .single();

      if (createError) throw createError;

      setFields(prev => [data, ...prev]);
      return data;
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const updateField = async (id: string, updates: Partial<CustomField>) => {
    if (!supabase) return false;

    try {
      const { error: updateError } = await supabase
        .from('account_fields')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (updateError) throw updateError;

      setFields(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  const deleteField = async (id: string) => {
    if (!supabase) return false;

    try {
      const { error: deleteError } = await supabase
        .from('account_fields')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      setFields(prev => prev.filter(f => f.id !== id));
      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  useEffect(() => {
    fetchFields();
  }, [fetchFields]);

  return {
    fields,
    loading,
    error,
    fetchFields,
    createField,
    updateField,
    deleteField
  };
}
